import type { OrderInfo } from '@@partner:types:app'
import { ORDER } from '@hive/status'

export type SortOption = 'slot_time' | 'order_id' | 'created_at'

export const SORT_OPTIONS: { value: SortOption; label: string }[] = [
  { value: 'slot_time', label: 'По времени слота' },
  { value: 'order_id', label: 'По номеру заказа' },
  { value: 'created_at', label: 'По дате создания' }
]

/**
 * Сортировка заказов
 */

export function sortOrders (orders: OrderInfo[], sortBy: SortOption): OrderInfo[] {
  return [ ...orders ].sort((a, b) => {
    switch (sortBy) {
    case 'slot_time': {
      const aTime = a.deliverySlot?.period?.from ? parseInt(a.deliverySlot.period.from) : Infinity
      const bTime = b.deliverySlot?.period?.from ? parseInt(b.deliverySlot.period.from) : Infinity
      return aTime - bTime
    }
    case 'order_id':
      return Number(a.id) - Number(b.id)
    case 'created_at':
      return new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
    default:
      return 0
    }
  })
}

/**
 * Определение цвета срочности на основе времени до слота и статуса заказа
 *
 * Размещён: Желтый < 30 мин, Красный < 15 мин
 * Собирается: Желтый < 15 мин, Красный < 5 мин
 * Собран / Ожидает оплаты: Желтый < 5 мин
 * Ожидает курьера / Доставляется: не подсвечиваем
 */

export function getUrgencyColor (order: OrderInfo): string | null {
  const now = Date.now()
  const slot = order.deliverySlot

  // Ожидает курьера / Доставляется - не подсвечиваем
  if (order.status === ORDER.WAITING_FOR_COURIER || order.status === ORDER.DELIVERY_IN_PROGRESS) {
    return null
  }

  if (!slot?.period?.from) {
    return null // нет слота - не подсвечиваем
  }

  const startTime = parseInt(slot.period.from)
  const timeLeftMs = startTime - now

  const FIVE_MIN = 5 * 60 * 1000
  const FIFTEEN_MIN = 15 * 60 * 1000
  const THIRTY_MIN = 30 * 60 * 1000

  switch (order.status) {
  case ORDER.CONFIRMED:
    // Размещён: Желтый < 30 мин, Красный < 15 мин
    if (timeLeftMs < FIFTEEN_MIN) return '#f5222d' // красный
    if (timeLeftMs < THIRTY_MIN) return '#faad14'  // желтый
    return null

  case ORDER.ASSEMBLY_IN_PROGRESS:
    // Собирается: Желтый < 15 мин, Красный < 5 мин
    if (timeLeftMs < FIVE_MIN) return '#f5222d'    // красный
    if (timeLeftMs < FIFTEEN_MIN) return '#faad14' // желтый
    return null

  case ORDER.ASSEMBLY_COMPLETE:
  case ORDER.WAITING_FOR_PAYMENT:
    // Собран / Ожидает оплаты: Желтый < 5 мин
    if (timeLeftMs < FIVE_MIN) return '#faad14'    // желтый
    return null

  default:
    return null
  }
}

/**
 * Группа статусов для Kanban-колонки
 */
export interface KanbanStatusGroup {
  key: string
  label: string
  statuses: number[]
}

/**
 * Группировка заказов по статусам
 */

export function groupOrdersByStatus (
  orders: OrderInfo[],
  statuses: number[]
): Map<number, OrderInfo[]> {
  const grouped = new Map<number, OrderInfo[]>()

  // Инициализация пустыми массивами для каждого статуса
  statuses.forEach(status => {
    grouped.set(status, [])
  })

  // Распределение заказов по статусам
  orders.forEach(order => {
    const statusOrders = grouped.get(order.status)
    if (statusOrders) {
      statusOrders.push(order)
    }
  })

  return grouped
}

/**
 * Группировка заказов по группам статусов для Kanban
 */
export function groupOrdersByStatusGroups (
  orders: OrderInfo[],
  groups: KanbanStatusGroup[]
): Map<string, OrderInfo[]> {
  const grouped = new Map<string, OrderInfo[]>()

  groups.forEach(group => {
    grouped.set(group.key, [])
  })

  orders.forEach(order => {
    for (const group of groups) {
      if (group.statuses.includes(order.status)) {
        grouped.get(group.key)!.push(order)
        break
      }
    }
  })

  return grouped
}
